import { useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';

import { createBulkFeeAttempt, updateBulkFeeAttempt } from './api';
import { UploadCsvValidationErrorType, parseUploadCsvValidationError } from './data';
import { BulkFeeAttemptDocument, type BulkFeeAttemptQuery } from './graphql/query.bulkFeeAttempt.generated';

type UseUploadCsvSubmitParams =
  | {
      variant: 'create';
      onSuccess: (bulkFeeAttempt: BulkFeeAttemptQuery['firm']['bulkFeeAttempt']) => void | Promise<void>;
    }
  | {
      variant: 'update';
      id: string;
      onSuccess: (bulkFeeAttempt: BulkFeeAttemptQuery['firm']['bulkFeeAttempt']) => void | Promise<void>;
    };

type UploadCsvSubmitValues = {
  csvFile: File | null;
  name?: string;
};

export const useUploadCsvSubmit = (params: UseUploadCsvSubmitParams) => {
  const [isSubmittingUploadedCsv, setIsSubmittingUploadedCsv] = useState(false);
  const [uploadCsvValidationError, setUploadCsvValidationError] = useState<UploadCsvValidationErrorType | null>(null);
  const { refetch: fetchBulkFeeAttempt } = useGraphQLQuery(BulkFeeAttemptDocument, {
    skip: true,
    variables: { id: '' },
  });

  const handleSubmit = async (values: UploadCsvSubmitValues) => {
    if (!values.csvFile) {
      setUploadCsvValidationError(UploadCsvValidationErrorType.GenericError);
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    setIsSubmittingUploadedCsv(true);
    setUploadCsvValidationError(null);

    try {
      const result =
        params.variant === 'create'
          ? await createBulkFeeAttempt({
              csvFile: values.csvFile,
              name: values.name || '',
            })
          : await updateBulkFeeAttempt({
              id: params.id,
              csvFile: values.csvFile,
            });

      if (result.success) {
        const bulkFeeAttemptResult = await fetchBulkFeeAttempt({ id: result.data.id });
        const bulkFeeAttempt = bulkFeeAttemptResult.data?.firm.bulkFeeAttempt ?? null;

        await params.onSuccess(bulkFeeAttempt);
        setIsSubmittingUploadedCsv(false);
      } else if (result.errors.csvFile) {
        const validationErrorType =
          parseUploadCsvValidationError(result.errors.csvFile) ?? UploadCsvValidationErrorType.GenericError;
        setUploadCsvValidationError(validationErrorType);
        setIsSubmittingUploadedCsv(false);
        window.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        setUploadCsvValidationError(UploadCsvValidationErrorType.GenericError);
        setIsSubmittingUploadedCsv(false);
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    } catch (_error) {
      setUploadCsvValidationError(UploadCsvValidationErrorType.GenericError);
      setIsSubmittingUploadedCsv(false);
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  return {
    isSubmittingUploadedCsv,
    uploadCsvValidationError,
    handleSubmit,
  };
};
